// ════════════════════════════════════════════════════════════════════
// TERROR — MINERIUM ONE: THE SCRIPT
// The Minerium of Sir H'osh Vrown, start to finish, in story order.
// ────────────────────────────────────────────────────────────────────
// caP'n, 2026-08-18: *"do the Minerium 1 Story pass in its entirety."*
//
// ── WHY THIS IS ITS OWN FILE ─────────────────────────────────────────
// The words were scattered across three files and none of them were
// about this story. `EndlessDepths` owns the gatekeepers — but its
// V'ael says "the descent ends here," which is a line about a LADDER,
// not about a marsh. `storyData` owns the Inner villains. `worldData`
// owns the map. So a boss reused on the world map was saying Depths
// lines in a Minerium, and the only alternative on offer was to fork
// the cast and keep two V'aels in sync forever.
//
// So: the CAST stays where it lives and is never re-typed. This file
// holds only what is different HERE — one overlay, keyed by node id,
// merged in `opponentForNode`. Same character, different scene. Edit a
// character's stats in one place; edit what they say in this Minerium
// in another. Minerium 2 gets `minerium2.js` and touches nothing.
//
// ── THE SCRIPT FORMAT ────────────────────────────────────────────────
//   plain line          → the scene's speaker
//   "Name: line"        → THAT person, for this line only (three-way
//                         scenes without three-way plumbing)
//   "[line]"            → the player — Lyraei & Lestre, who say "we"
//   "*line*"            → a stage direction (also works mid-sentence)
// See StoryCutscene.readLine(). This is the format caP'n already
// writes scripts in, which is the entire reason it is the format.
//
// ── WHAT IS AUTHORED AND WHAT IS V'AEL'S ─────────────────────────────
// ⭐ = caP'n's own words, verbatim, do not paraphrase.
// Everything else is placeholder written against TERROR_STORY_SPINE.md
// and its tiers. Where a line gestures at a 🔴 NEVER fact it is marked,
// so a future edit knows it is load-bearing and not decoration.
//
// ── THE THREE THINGS THIS SCRIPT MUST NOT SAY ────────────────────────
//   🔴 X'aol. Not once. Not obliquely enough to be decoded.
//   🔴 That J'ynxi's seal never fit, or that she is fond of the one it
//      was built for.
//   🔴 That the lie is a kindness — that the Sol is recoverable BECAUSE
//      a Solrend tethers it to Necropolis. The player may see a
//      character behave as though it were true. Nobody explains it.
// ════════════════════════════════════════════════════════════════════

// ─────────────────────────────────────────────────────────────
// THE OVERLAY — node id → what is said at that node, here
// ─────────────────────────────────────────────────────────────
// A node with no entry keeps whatever its boss already says, which is
// how the other forty-odd nodes stay quiet rather than wrong.
//
// `speaker` / `speakerTitle` / `accent` are optional: absent, the
// resolved boss's own name, title and colour are used. They exist for
// the scenes with no boss in them at all.

// ═══════════════════════════════════════════════════════════════
// ⚠ RE-KEYED 2026-08-21 — caP'n re-traced the whole map
// ═══════════════════════════════════════════════════════════════
// Every key in this object used to be a hand-written slug on the old
// 55-node graph — `veil_gate`, `drowned_arch`, `halls_seal`. caP'n
// re-drew the world and numbered it, so the keys are his node CODES
// now, and each scene has been re-homed onto the node it fits rather
// than the node it happened to be on:
//
//   veil_gate    → AAC-01  the Roadhead, still the first thing you meet
//   veil_watch   → ABC-10  the Wading Line — "one shadow between you"
//   veil_kiln    → AZS-53  the Last Kiln — and T'yrent is the foe there
//   drowned_arch → ACS-19  the Tally Vault — "I have you in the ledger"
//   vaults_arch  → ABC-11  the Still Reach — V'ael, and water that lags
//   halls_seal   → AFC-37  the Herald's Field
//   throne_seal  → ACL-26  the Black Chamber
//   dais_jewel   → AZS-56  the Crimson Jewel
//
// ⚠ A SCENE ON A DEAD KEY IS INVISIBLE AND SILENT. `sceneFor` returns
// undefined and `opponentForNode` quietly falls through to the foe's
// own words — no warning, no error, just eight authored conversations
// that never happen. That is why this list is written out.
//
// ⭐ AND FOUR LINES OF PROSE MOVED WITH THEM. Three scenes named
// places the re-trace deleted — "the Gardens", the pipes, the Keep —
// so those lines now name what is actually there. Everything else is
// caP'n's placeholder script, untouched, waiting to be written over.
export const SCENES = {

  // ══ ZONE A · BONEYARD SOUTH, and ZONE B beyond it ═══════════════
  // The soldiers here are the OLD Minerium's, not Sir H'osh Vrown's.
  // He was granted a kingdom that came with staff who never agreed to
  // him. That is why nobody in the south yard mentions him with any respect,
  // and it is the whole reason he behaves like a man waiting to be
  // asked to leave. (Reconciliation, flagged in storyData.js.)

  "AAC-01": {   // The Roadhead — the first thing in the world
    approach: [
      "*The Outer Gate has not been a gate for a long time. It is two posts and an oath.*",
      "Halt.",
      "There is a man inside who calls this place his. He has a paper that says so.",
      "I have a gate. The gate disagrees.",
      "[We are not here about the paper. We are here for our children.]",
      "Everyone who comes through is here for something. The gate does not sort them.",
    ],
    onWin: [
      "...Go.",
      "The oath was to hold. It was never to win.",
    ],
    onLoss: [
      "The gate holds. As it always has.",
      "Come back when a Solrend has cost you something.",
    ],
  },

  "ABC-10": {   // The Wading Line
    approach: [
      "Two of you. One shadow between you.",
      "That is either love or a very old wound. I have stopped being able to tell them apart.",
      "[We are one thing. We have always been one thing.]",
      "Then it will only hurt once.",
    ],
    onWin: [
      "Hm.",
      "Your shadow went on ahead of you. It has been going on ahead of you for some time.",
      "Follow it. It knows the way better than either of you.",
    ],
    onLoss: [
      "Even your shadow sat down.",
      "Rest. The Marsh is patient. So am I.",
    ],
  },

  "AZS-53": {   // The Last Kiln — T'yrent, and the foe row agrees
    approach: [
      "The last fire in this place, and they hung it in the dark on a slab of red stone.",
      "I did not choose the post. I chose the Minerium.",
      "The difference is that the Minerium is gone and I am still standing in it.",
      "*T'yrent looks at the two of you for a long moment.*",
      "You are looking for someone. Do not tell me who. If I hear it I will have to care.",
    ],
    onWin: [
      "...Ah.",
      "Go on, then. The kiln is finished with you.",
      "And if you find them — do not come back this way to tell me. I have had enough news for one age.",
    ],
    onLoss: [
      "Fire has a hierarchy. Today you are beneath it.",
      "Come back when the Chaos Dragon bores you.",
    ],
  },

  // ══ ZONE C · THE TALLY VAULT ══════════════════════════
  // 🟠 THE FIRST HARD PROOF. Sov'rn records; Sov'rn does not
  // interpret. Two entries, four words each, and the whole of the
  // children's situation is in the gap between them. Nothing is
  // stated. This is the tier working exactly as intended.

  "ACS-19": {   // The Tally Vault — Vaunt already has you in the ledger
    approach: [
      "I have you in the ledger already.",
      "Arrived separately. Left together. That was some time ago, and it was not you — but the hand is the same.",
      "[Our children.]",
      "Two entries. Very short ones. Children write short entries.",
      "You may read them when you have earned the reading.",
    ],
    onWin: [
      "*Sov'rn turns the ledger around without hurrying.*",
      "The elder wrote: 'we are fine.'",
      "The younger wrote: 'we are not fine.'",
      "I did not ask which was true. I only record.",
    ],
    onLoss: [
      "Then you are not in the ledger yet.",
      "Come back. I would like to write you down.",
    ],
  },

  // ══ ZONE B · THE STILL REACH ══════════════════════════════
  // 🟠 V'AEL, HERE, IS THE AVATAR THEY TURNED DOWN. Sir H'osh Vrown
  // opens the whole story by mocking them for arriving "without V'ael's
  // Avatar to assist you" — so the offer was made and refused before
  // the first node. This scene is that refusal catching up with them.
  //
  // 🔴 She never once mentions that she and J'ynxi are friends. She
  // simply behaves like someone who is not worried, in a story where
  // everyone else is. Structure, not text.

  "ABC-11": {   // The Still Reach — V'ael, and the water that lags
    approach: [
      "You made it to the still water.",
      "I said you would. I also said it would take you longer than this, so we are each half right.",
      "[You are here.]",
      "*She does not get up.*",
      "I am always here. I offered you this one, and you said no.",
      "So it sat down at the edge of the water and waited to find out whether you meant it.",
      "[We meant it.]",
      "Good. Then mean it at me.",
    ],
    onWin: [
      "...",
      "You are better than you were when you left the Plaza. That is not a compliment. It is a measurement.",
      "Go left.",
      "The door on the left has been open the whole time, and something small went through it.",
    ],
    onLoss: [
      "No.",
      "Not yet, and not like that.",
      "The offer still stands, by the way. It will keep standing.",
      "That is the entire point of an offer.",
    ],
  },

  // ══ ZONE F · BONEYARD NORTH ═════════════════════════
  // Rük does not negotiate and does not taunt — storyData says so, and
  // a scene that ignores it would be a louder mistake than no scene.
  // So Rük is given six words in total. The stage directions carry the
  // rest, and the six words are the clue.

  "AFC-37": {   // The Herald's Field
    approach: [
      "*Something along the line of candles stops moving, which is how you learn that it had been moving.*",
      "...Small ones came through.",
      "*It offers nothing else. It does not seem to be threatening you. It seems to be finished with a fact.*",
      "[Where.]",
      "Through.",
    ],
    onWin: [
      "*It does not fall so much as stop.*",
      "Through. I said.",
    ],
    onLoss: [
      "*The line of candles goes on burning level.*",
    ],
  },

  // ══ ZONE C · SHV's MAUSOLEUM ════════════════════════════════════
  // ⚠ THE SEAL IS EMPTY, AND THAT IS THE POINT. J'ynxi has been out
  // since before the game began — the children broke her out, and she
  // is upstairs in the Plaza the entire time the player is down here.
  // The old dungeon-7 script had the player RELEASE her at the end;
  // that ending is now canonically impossible, so the Seal fields a
  // leftover of her instead and knows it.
  //
  // 🟠 "I told them the truth. Most of it." is the closest THE LIE is
  // ever allowed to come to being said. It never gets closer.
  // 🟠 "Weather doesn't threaten you" is her whole character in five
  // words: True Chaos, nature's equaliser, neither kind nor cruel.

  "ACL-26": {   // The Black Chamber
    approach: [
      "*The chamber is obsidian the whole way down, and the seat at the bottom of it is empty.*",
      "*It has been open for some time. Whatever is sitting in it is sitting in it the way a coat sits on a chair.*",
      "Ohhh. You came all the way down here.",
      "[You are upstairs. We saw you upstairs.]",
      "I'm a lot of places. It's a bad habit.",
      "This one's just what got left in the chair. Play it anyway — it's been lonely.",
    ],
    onWin: [
      "There. Now the room is quiet.",
      "Your two came through here, you know. They didn't stop.",
      "I told them the truth.",
      "*The shape considers this for exactly as long as it takes to say.*",
      "Most of it.",
    ],
    onLoss: [
      "Aw.",
      "With every victory, J'ynxi twists. With every defeat, J'ynxi turns.",
      "That isn't a threat. Weather doesn't threaten you.",
    ],
  },

  // ══ THE CRIMSON JEWEL · THE CENTRE OF THE DAIS ══════════════════
  // ⭐ THE AUTHORED CONFRONTATION, verbatim, plus two lines of preface.
  //
  // ⚠ WHY THE PREFACE EXISTS. His Lyt Avatar at the door says the
  // first of these lines word for word — caP'n wrote one exchange and
  // it got split across the doorman and the man. Rather than cut
  // authored text, the repeat is made deliberate: the door is an echo
  // of him with no weight behind it, and he says so before repeating
  // himself. One line, and a duplicate becomes characterisation.
  // If caP'n would rather the door not say it at all, delete
  // hosh_lyt.dialogue.approach[0] and this preface together.

  "AZS-56": {   // The Crimson Jewel — the end of the story
    approach: [
      "*The fare is paid and the world folds. The jewel opens onto a chamber the size of a held breath, and he is already standing in it.*",
      "You have heard this once already. My door says it for me. It says it badly.",
      "Bold of you two to enter without V'ael's Avatar to assist you, or her wretched groundskeeper — he has so many... that foul beast.",
      "[We declined the offer. Do you think us so weak as not to be able to rescue our children, no matter where they are?]",
      "*smirks* You're either naive or...",
    ],
    onWin: [
      "...",
      "Then take them. They were never mine to keep.",
      "Tell V'ael I was courteous.",
    ],
    onLoss: [
      "Naive, then.",
      "I did wonder.",
    ],
  },
};

export const sceneFor = (nodeId) => SCENES[nodeId] || null;

// ─────────────────────────────────────────────────────────────
// THE INTERLUDES — story that is not a duel
// ─────────────────────────────────────────────────────────────
// Three of Minerium One's beats have no opponent attached: two traces
// of the children, the confrontation that hands over J'ynxi's coin,
// and the ending. They fire AFTER a clear, they are stamped once ever
// (profiles/{uid}/worldMap/beats/{id} — a field under an existing
// profile node, so no rules deploy and never a new top-level node),
// and they chain: `next` on a scene is the scene that plays when it
// finishes.
//
// `grant` is a declarative hand-over, read by the caller. It is a row
// rather than a branch precisely because the next one will not be a
// coin — THE EXCEPTION LAW.

export const INTERLUDES = [

  // ── MOON'S TRACE ────────────────────────────────────────────────
  // 🟢 Story One concludes to no avail: they do not find the children.
  // So every "child" node on this map is a TRACE, never a reunion. The
  // engine calls the stamp `found/` and it stays called that — what is
  // found is the trail, and both readings are true.
  {
    id: "m1_trace_moon",
    afterNode: "ABC-11",
    speaker: "The Left Door",
    accent: "#22d3ee",
    lines: [
      "*The reflection is half a second behind everywhere on this water except one place. In that one place it is not behind at all. It is waiting.*",
      "*Something small is caught on the hinge. Cyan. Still warm.*",
      "[Moon.]",
      "[She came through here. She came through here and she kept going.]",
      "*You take it. It is not her. It is the second-best thing there is.*",
    ],
    next: "m1_jynxi_coin",
  },

  // ── THE CONFRONTATION ───────────────────────────────────────────
  // ⭐ caP'n's beat, staged. Canon puts this in the Plaza — "the
  // parents confront V'ael after a few missions" — so it fired here
  // instead, where V'ael already is, on the node the trace fires from:
  // they find what Moon left, and THAT is what makes them go and
  // demand answers.
  //
  // ⭐ RULED AND CLOSED, 2026-08-19. V'yen's dialogue window now
  // exists, and the old note here said moving this beat into it was
  // "a change of two fields." caP'n: *"yeah in Minerium is better,
  // maybe right after a specific node it just starts it."* It already
  // does — this is an interlude chained off `m1_left_door`, which is
  // exactly "right after a specific node."
  //
  // So: DO NOT MOVE IT. And the reason is worth keeping, because the
  // next person to see V'yen's window will have the same idea I did:
  // a beat that fires on a PLAZA VISIT can be missed entirely by a
  // player who does not go back to the Plaza — and this one pays a
  // coin skin. A ⭐ beat and a grant that depend on the player
  // wandering somewhere optional is not a staging choice, it is a
  // hole.
  //
  // 🔴 Never stated here: that V'ael and J'ynxi are friends, that
  // Pierre knows, or why the Sol is recoverable. V'ael simply answers
  // a question nobody asked her how she could possibly know.
  {
    id: "m1_jynxi_coin",
    speaker: "J'ynxi",
    speakerTitle: "?",
    accent: "#d4a84b",
    grant: { coinSkin: "jynxis_grin", source: "story:jynxi_gift" },
    lines: [
      "*You go back down through the black spires the way you came, which takes no time at all, because she has not moved.*",
      "[That was her. In the Plaza. Behind the counter at V'yen's, every single day, and you said nothing.]",
      "V'ael: I said a great many things. None of them were questions you asked.",
      "*Someone clears their throat. She is standing exactly where nobody was standing.*",
      "*A false nose. Enormous brows. Glasses you could stop a duel with.*",
      "Who, me?",
      "[...]",
      "V'ael: One of them loses a Sol. That part was never a trick — it is going to happen, and it is going to happen to Necropolis.",
      "V'ael: It can be gotten back. I am not going to explain that to you.",
      "V'ael: And their Solrend is on hold. Held, not cancelled. When Sol Survivor is finished they finish the duel, and the duel decides which of them it was.",
      "[Then we finish this and we get there first.]",
      "It's futile, sweetheart.",
      "You already did the part you get to do. Put your faith in your parenting — and in what they do with it.",
      "[No.]",
      "[We will track them down with you or without you.]",
      "*She laughs. It is a real laugh, which is somehow worse.*",
      "*She flips something at you underhanded. You catch it without deciding to.*",
      "*The nose is gone. The brows are gone. She is in a long coat and a low hat now, and she was not a moment ago.*",
      "Go get 'em, kid. I know you can do it.",
    ],
  },

  // ── SUN'S TRACE ─────────────────────────────────────────────────
  {
    id: "m1_trace_sun",
    afterNode: "AFC-37",
    // ⚠ SPEAKER RE-NAMED 2026-08-21. This was "The Cistern" and it
    // fires after AFC-37, which is THE HERALD'S FIELD in Boneyard
    // North — the cistern, the valves and the pipe were all in the
    // Pipe-Lich Graveyard, which the re-trace deleted. A speaker plate
    // naming a room that does not exist is the seam showing.
    speaker: "The Standard",
    accent: "#e8b84b",
    lines: [
      "*The standard in the middle of the field is not the one that was planted here. Someone swapped it, and swapped it carefully, and took the old one with them.*",
      "*That takes either a very long time or a very good guess.*",
      "*There is a mark cut into the shaft at the height of a child. Gold. Fresh enough to still carry a burr.*",
      "[Sun.]",
      "[He was showing us the way. He knew we would come.]",
      "*You take the reading. It is not him. You are getting very tired of the second-best thing.*",
    ],
  },

  // ── THE END OF MINERIUM ONE ─────────────────────────────────────
  // 🟢 Concludes to no avail — with a clue. 🔴 He is getting rid of
  // you, and he is the one who comes to punish you in Minerium 4.
  // The player is told the tip and the warning. The player is not told
  // that the tip IS the warning.
  {
    id: "m1_champion",
    afterNode: "AZS-56",
    // ⭐ MARSHAL MALONE. Named by caP'n, 2026-08-19, over V'ael's draft:
    //   "he's going to be more like a Sheriff -- still stern like SHV
    //    but more western, without the accent."
    //
    // So the REGISTER is western and the MOUTH is not. No dropped g's,
    // no "ain't", no "reckon", no drawl written into the spelling —
    // caP'n ruled the accent out explicitly and an accent typed into
    // dialogue is the one thing that cannot be un-heard later. What
    // makes him western is JURISDICTION: he thinks in territory,
    // paperwork, warnings issued and warnings spent. Sir H'osh Vrown
    // is a soldier who lost. This is the man who signs for soldiers.
    //
    // He is stern the way a lawman is stern, which is not the way SHV
    // is stern: SHV performs it, Malone is simply out of interest in
    // performing anything. He says a thing once. That is the whole
    // characterisation and it is also 🔴 the plot — the once is spent
    // here, and in Minerium 3 he collects.
    speaker: "Marshal Malone",
    speakerTitle: "of the Great Omnis",
    accent: "#f2c14e",
    lines: [
      "*The chamber does not empty. It fills. Somebody very large has been waiting for the noise to stop.*",
      "*He does not draw. He looks at the room the way a man looks at a field he is going to have to walk.*",
      "You put down our Colonel.",
      "[He was in the way.]",
      "*He accepts this immediately, which is unsettling.*",
      "He was. He generally is. It is most of what he is for.",
      "Post's open. It's yours if you want it.",
      "This Minerium, the next one over, and paper saying so. Sir H'osh Vrown gets somewhere quieter and does not complain where you can hear him.",
      "[No.]",
      "[We want our children back. That is the whole of it.]",
      "*A long pause. He was expecting this and had the second thing ready anyway.*",
      "They are not in my territory. I would know. I would have had to sign for them.",
      "There is another Minerium. Not mine, not his, and at worst it makes you rich.",
      "Whether it holds anything that saves two Esclings — I could not tell you. I have no knowledge of it at all.",
      "*He steps aside from a door that was not there.*",
      "Ride out that way instead.",
      "*He waits until you are looking at him and not at the door.*",
      "Do not come back into a Minerium of ours uninvited. That is your warning, and you have had it.",
      "I do not issue a second one.",
    ],
  },
];

export const INTERLUDE_BY_ID = Object.fromEntries(INTERLUDES.map(b => [b.id, b]));

// The interlude a clear just earned, or null. `fired` is the set of
// beat ids already stamped, so a beat plays exactly once ever even
// though a node can be re-walked forever.
export function interludeAfter(nodeId, fired) {
  const seen = fired instanceof Set ? fired : new Set(Object.keys(fired || {}));
  return INTERLUDES.find(b => b.afterNode === nodeId && !seen.has(b.id)) || null;
}

// Follow `next` through the chain, dropping any link already stamped.
// Returns the whole run in play order, first included.
export function interludeChain(first, fired) {
  const seen = fired instanceof Set ? fired : new Set(Object.keys(fired || {}));
  const out = [];
  let cur = first;
  const guard = new Set();
  while (cur && !guard.has(cur.id)) {
    guard.add(cur.id);
    if (!seen.has(cur.id)) out.push(cur);
    cur = cur.next ? INTERLUDE_BY_ID[cur.next] : null;
  }
  return out;
}

const minerium1 = { SCENES, sceneFor, INTERLUDES, INTERLUDE_BY_ID, interludeAfter, interludeChain };
export default minerium1;
